import { Store } from 'lucide-react';
import { useAppState } from '@/app/appContext';
import { useT } from '@/contexts/I18nContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { NoBranchAccessState } from '@/components/common/DataErrorState';
import { cn } from '@/lib/utils';

/**
 * Unified empty state shown on every branch-required page when no branch/zone
 * is selected in the header selector. Replaces the assorted bare-text /
 * card-with-text "select a branch" prompts so the gate looks identical
 * everywhere. This is the NO-BRANCH gate only — do not use it for the
 * legitimate "branch selected but no data yet" states (e.g. report not
 * generated for a given day/week).
 */
export function BranchRequiredState({
  title,
  subtitle,
  className,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
}) {
  const { t } = useT();
  // In «Все филиалы» mode the honest headline is «режим недоступен на этой
  // странице», not «филиал не выбран» — the user DID pick something. The
  // subtitle (pick a branch in the header selector) fits both cases.
  const { allBranches, branchGroupId } = useAppState().zone;

  // A ?zone= the user has no access to lands here too: every gated page derives
  // its zoneId from useCurrentZone, which returns null for a branch outside the
  // user's list — indistinguishable from "nothing selected". Telling someone who
  // followed a shared link to "pick a branch" is misleading; say it is not theirs
  // (Alexey 09.09). Handled centrally so all 17 gated pages behave the same.
  const { noZoneAccess } = useCurrentZone();
  if (noZoneAccess) return <NoBranchAccessState className={className} />;

  // Выбрана группа — пишем про группу (Алексей 25.10 #2318), «Все филиалы» остался только суперпользователю.
  const defaultTitle = allBranches ? t(branchGroupId ? 'branchRequired.groupTitle' : 'branchRequired.allTitle') : t('branchRequired.title');
  return (
    <div
      className={cn(
        // Fill the content area below the header so the block sits VERTICALLY
        // CENTERED, not anchored high (min-h-[50vh] centered within only the top
        // half → the prompt "sat up top", Alexey 855). Wide enough that title +
        // subtitle don't each wrap to 2 lines on a big screen (796/2026-07-21).
        'mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-2xl flex-col items-center justify-center gap-3 px-4 py-12 text-center',
        className,
      )}
    >
      <span className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Store className="size-6" />
      </span>
      <h2 className="text-balance text-xl font-semibold tracking-tight text-foreground">
        {title ?? defaultTitle}
      </h2>
      <p className="max-w-xl text-balance text-sm text-muted-foreground">
        {subtitle ?? t('branchRequired.subtitle')}
      </p>
    </div>
  );
}

export default BranchRequiredState;
